import React, { Fragment, useContext, useRef, useState } from 'react';
import { ICartItem, ILabel, ITypeCartFields, OfferContext, VignetteContext, getLabelValue } from '@adac-mp/shared';
import {
	Badge,
	Button,
	ChevronDown,
	HintMessage,
	ProductSummaryContainer,
	Typography,
	getHintContent,
} from '../../index';
import { renderSectionTollProductMessage } from '../../utils/hintAndErrorMessages';

interface IProductsSectionProps {
	openProductKey?: string;
	sectionIndex: number;
	orderedCartItems: ICartItem[];
	moduleType: string;
	hasFuelType: boolean;
	hasCategories: boolean;
	hasSpecialPlates?: boolean;
	memberNo?: string;
	handleToggle: (currentKey?: string) => void;
}

const renderStackedViewButton = (
	orderedCartItems: ICartItem[],
	showStackedView: boolean,
	setShowStackedView: React.Dispatch<React.SetStateAction<boolean>>,
	labels: ILabel[] | undefined,
) => (
	<Button
		variant="custom"
		className="!justify-end text-gray-200 hover:text-gray-100 bg-transparent before:content-none border-none"
		style={{
			marginTop: showStackedView ? orderedCartItems.length * 8 : 0,
		}}
		onClick={() => {
			setShowStackedView((prev: boolean) => !prev);
		}}>
		{showStackedView
			? getLabelValue('mp.showMore', labels) || 'Alle Produkte anzeigen'
			: getLabelValue('mp.showLess', labels) || 'Weniger Produkte anzeigen'}
		<ChevronDown rotate={!showStackedView} />
	</Button>
);

export const SortedProductsSection = ({
	openProductKey,
	moduleType,
	orderedCartItems,
	sectionIndex,
	memberNo,
	hasCategories,
	hasFuelType,
	hasSpecialPlates,
	handleToggle,
}: IProductsSectionProps) => {
	const { labels } = useContext(OfferContext);
	const { contentfulLabels, cartItems } = useContext(VignetteContext);
	const cartLabels = contentfulLabels?.cart as ITypeCartFields;
	const [showStackedView, setShowStackedView] = useState(orderedCartItems.length > 2);
	const [stackIsHovered, setStackIsHovered] = useState(false);
	const hintMessageRef = useRef<HTMLDivElement | null>(null);
	const areOverlapping = orderedCartItems.some(
		({ cartOverlapping, offer }) => cartOverlapping && offer.moduleTypeId === 2,
	);
	const flexWithoutVignette =
		!orderedCartItems.some(({ offer }) => offer.moduleTypeId === 2) &&
		orderedCartItems.some(({ offer }) => offer.moduleTypeId === 4 || offer.moduleTypeId === 3);
	//one hint message is for overlapping vignettes, the other one is if the user added flex service without any vignettes in the cart

	const getSectionHeight = () => {
		const baseHeight = 172;
		const msgHeight = hintMessageRef.current?.getBoundingClientRect().height ?? 0;
		return areOverlapping || flexWithoutVignette ? msgHeight + 16 + baseHeight : baseHeight; //height of hint msg plus gap
	};

	const renderOverlappingHintMessage = () =>
		areOverlapping && (
			<HintMessage
				ref={hintMessageRef}
				padding="large"
				hints={getHintContent(
					'multipleVignettes',
					[
						{
							tip: 'Hinweis:',
							message:
								'Es befinden sich mehrere Digitale Vignetten mit sich überschneidenden Gültigkeitszeiträumen in Ihrem Warenkorb.',
						},
					],
					cartLabels?.hints,
				)}
			/>
		);

	return (
		<Fragment key={`${moduleType}-${sectionIndex}`}>
			<div
				key={moduleType}
				className={`flex flex-col gap-3 relative z-50 ease-in-out duration-500 ${
					sectionIndex > 0 ? 'mt-3.5' : ''
				}`}
				style={{
					maxHeight: showStackedView ? getSectionHeight() : 3000,
				}}>
				<Typography className="pl-4" color="title">
					{getLabelValue('mp.digital', labels) || 'Digitale'}{' '}
					{orderedCartItems[0].offer.moduleTypeId === 2
						? moduleType
						: getLabelValue('mp.tollSection', labels)}{' '}
					{orderedCartItems.length > 2 && <Badge className="ml-1.5">{orderedCartItems.length}</Badge>}
				</Typography>
				{renderOverlappingHintMessage()}
				{renderSectionTollProductMessage(cartItems, cartLabels, hintMessageRef)}
				{orderedCartItems.map((cartItem, index) => (
					<ProductSummaryContainer
						key={cartItem.uuid}
						multipleProducts
						cartItem={cartItem}
						index={index}
						openProductKey={openProductKey}
						showStackedView={showStackedView}
						stackIsHovered={stackIsHovered}
						hasCategories={hasCategories}
						hasSpecialPlates={hasSpecialPlates}
						hasFuelType={hasFuelType}
						memberNo={memberNo}
						handleToggle={handleToggle}
						setStackIsHovered={setStackIsHovered}
						setShowStackedView={setShowStackedView}
					/>
				))}
			</div>
			{orderedCartItems.length > 2 &&
				renderStackedViewButton(orderedCartItems, showStackedView, setShowStackedView, labels)}
		</Fragment>
	);
};
